import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Badge, Form } from 'react-bootstrap';
import { Users, UserPlus, BarChart2, List, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import VisitorStatCard from './VisitorStatCard';
import VisitorLogCard from './VisitorLogCard';
import { API_ENDPOINTS } from '../../../../config/api';
import './VisitorManagement.css';

const calculateAge = (dobString) => {
    if (!dobString) return null;
    const today = new Date();
    const birthDate = new Date(dobString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

const VisitorDashboard = () => {
    const [hoveredBar, setHoveredBar] = useState(null);
    const [totalVisitors, setTotalVisitors] = useState(0);
    const [todayVisitors, setTodayVisitors] = useState(0);
    const [incompleteProfiles, setIncompleteProfiles] = useState(0);
    const [totalVisits, setTotalVisits] = useState(0);
    const [activeView, setActiveView] = useState("log");
    const [graphPeriod, setGraphPeriod] = useState("weekly");
    const [graphData, setGraphData] = useState([]);
    const [windowStart, setWindowStart] = useState(0);
    const [selectedBar, setSelectedBar] = useState(null);
    const [selectedVisitors, setSelectedVisitors] = useState([]);
    const [loadingVisitors, setLoadingVisitors] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState("All Branches");

    const windowSize = graphPeriod === "weekly" ? 7 : graphPeriod === "monthly" ? 6 : 5;

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        fetchGraphData();
        setSelectedBar(null);
        setSelectedVisitors([]);
    }, [graphPeriod, selectedBranch]);

    useEffect(() => {
        if (graphData.length > 0) {
            setWindowStart(Math.max(0, graphData.length - windowSize));
        }
    }, [graphData, graphPeriod]);

    const fetchGraphData = async () => {
        try {
            const type = graphPeriod === "weekly" ? "daily" : graphPeriod === "monthly" ? "monthly" : "yearly";
            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${BASE_URL}/api/visitors/graph-data?type=${type}&branch=${encodeURIComponent(selectedBranch)}`);
            const data = await response.json();
            setGraphData(data.data || []);
        } catch (err) {
            console.error('Error fetching graph data:', err);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await fetch(API_ENDPOINTS.VISITORS.STATS);
            const data = await response.json();
            setTotalVisitors(data.total);
            setTodayVisitors(data.today);
            setIncompleteProfiles(data.incompleteProfiles);
            setTotalVisits(data.totalVisits);
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const chartData = graphData
        .slice(windowStart, windowStart + windowSize)
        .map(d => ({ name: d.label, Visitors: d.count, isoDate: d.isoDate, isoMonth: d.isoMonth, isoYear: d.isoYear }));

    const totalData = graphData;
    const canGoBack = windowStart > 0;
    const canGoForward = windowStart + windowSize < totalData.length;

    const handleBarClick = async (barData) => {
        if (!barData) return;
        const clickedLabel = barData.name;

        if (selectedBar === clickedLabel) {
            setSelectedBar(null);
            setSelectedVisitors([]);
            return;
        }

        setSelectedBar(clickedLabel);
        setLoadingVisitors(true);
        setSelectedVisitors([]);

        try {
            const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            let url = `${BASE_URL}/api/visitors/by-date?`;
            if (barData.isoDate) url += `isoDate=${barData.isoDate}`;
            else if (barData.isoMonth) url += `isoMonth=${barData.isoMonth}`;
            else if (barData.isoYear) url += `isoYear=${barData.isoYear}`;

            url += `&branch=${encodeURIComponent(selectedBranch)}`;

            const response = await fetch(url);
            const data = await response.json();
            setSelectedVisitors(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching visitors by date:', err);
            setSelectedVisitors([]);
        } finally {
            setLoadingVisitors(false);
        }
    };

    const periodLabels = { weekly: "Weekly (7 Days)", monthly: "Monthly", yearly: "Yearly" };
    const periodVisitLabel = graphPeriod === "weekly" ? "Visits That Day" : graphPeriod === "monthly" ? "Visits That Month" : "Visits That Year";

    return (
        <div className="main-content" style={{ position: 'relative', zIndex: 1 }}>
            <Container className="py-4 px-4" style={{ minHeight: '100vh', position: 'relative' }}>

                <div className="mb-4">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <div style={{ width: '6px', height: '24px', borderRadius: '3px', background: 'linear-gradient(180deg, #ac2e62, #c34e7f)' }} />
                        <h2 className="mb-0 fw-bold" style={{ color: '#2c3e50', fontSize: '1.5rem', letterSpacing: '-0.5px' }}>Visitor Management</h2>
                    </div>
                    <p style={{ color: '#7f8c9b', fontSize: '0.9rem', marginLeft: '16px', marginBottom: 0 }}>Track and manage NGO premises visitors</p>
                </div>

                <Row className="g-3 mb-4">
                    <Col xs={6} sm={3}>
                        <VisitorStatCard title="Total Visitors" value={totalVisitors} icon={Users} color="#ac2e62" />
                    </Col>
                    <Col xs={6} sm={3}>
                        <VisitorStatCard title="Visitors Today" value={todayVisitors} icon={UserPlus} color="#28a745" />
                    </Col>
                    <Col xs={6} sm={3}>
                        <VisitorStatCard title="Incomplete Profiles" value={incompleteProfiles} icon={Users} color="#e67e22" />
                    </Col>
                    <Col xs={6} sm={3}>
                        <VisitorStatCard title="Total Visits" value={totalVisits} icon={UserPlus} color="#3498db" />
                    </Col>
                </Row>

                <div className="d-flex gap-2 mb-4">
                    <button onClick={() => setActiveView("log")} className={`db1-toggle-btn ${activeView === "log" ? "active" : "inactive"}`}>
                        <List size={15} /> Visitor Log
                    </button>
                    <button onClick={() => setActiveView("graph")} className={`db1-toggle-btn ${activeView === "graph" ? "active" : "inactive"}`}>
                        <BarChart2 size={15} /> View Graph
                    </button>
                </div>

                {activeView === "log" && (
                    <Row><Col xs={12}><VisitorLogCard onStatsRefresh={fetchStats} /></Col></Row>
                )}

                {activeView === "graph" && (
                    <div className="db1-graph-container">
                        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                            <div>
                                <h6 className="fw-bold mb-0" style={{ color: '#2c3e50' }}>Visitor Statistics</h6>
                                <p style={{ color: '#7f8c9b', fontSize: '0.82rem', marginBottom: 0 }}>Click a bar to see visitor details</p>
                            </div>
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <Form.Select
                                    value={selectedBranch}
                                    onChange={(e) => setSelectedBranch(e.target.value)}
                                    style={{ width: '200px', fontSize: '0.85rem', padding: '6px 12px', borderRadius: '20px', border: '1px solid #dee2e6' }}
                                >
                                    <option value="All Branches">All Branches</option>
                                    <option value="Maainagari">Maainagari</option>
                                    <option value="Shree Manashanti Chhatralaya">Shree Manashanti Chhatralaya</option>
                                    <option value="TMGF Central Office">TMGF Central Office</option>
                                    <option value="Maainagari - Solapur">Maainagari - Solapur</option>
                                    <option value="Maainagari - Goa">Maainagari - Goa</option>
                                </Form.Select>
                                {["weekly", "monthly", "yearly"].map(period => (
                                    <button key={period} onClick={() => setGraphPeriod(period)} className={`db1-period-btn ${graphPeriod === period ? "active" : "inactive"}`}>
                                        {periodLabels[period]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <button onClick={() => { setWindowStart(prev => Math.max(0, prev - windowSize)); setSelectedBar(null); setSelectedVisitors([]); }} disabled={!canGoBack} className={`db1-nav-btn ${canGoBack ? "enabled" : "disabled"}`}>←</button>
                            <span style={{ fontSize: "13px", color: "#7f8c9b", fontWeight: 600 }}>
                                {chartData.length > 0 ? `${chartData[0]?.name} → ${chartData[chartData.length - 1]?.name}` : "No data"}
                            </span>
                            <button onClick={() => { setWindowStart(prev => Math.min(totalData.length - windowSize, prev + windowSize)); setSelectedBar(null); setSelectedVisitors([]); }} disabled={!canGoForward} className={`db1-nav-btn ${canGoForward ? "enabled" : "disabled"}`}>→</button>
                        </div>

                        <ResponsiveContainer width="100%" height={320} style={{ outline: 'none' }}>
                            <BarChart data={chartData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }} style={{ outline: 'none' }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={{ stroke: '#eee' }} />
                                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} axisLine={{ stroke: '#eee' }} />
                                <Tooltip contentClassName="db1-chart-tooltip" cursor={{ fill: "rgba(172,46,98,0.04)" }} />
                                <Bar 
                                    dataKey="Visitors" 
                                    radius={[8, 8, 0, 0]} 
                                    maxBarSize={60} 
                                    onClick={(barData) => handleBarClick(barData)} 
                                    isAnimationActive={false}
                                    activeBar={false}
                                    style={{ cursor: "pointer", outline: "none" }}
                                >
                                {chartData.map((entry, index) => {
                                const isSelected = entry.name === selectedBar;
                                return (
                               <Cell
                                    key={`cell-${index}`}
                                    fill={
                                        hoveredBar === index
                                            ? "#c34e7f"
                                            : isSelected
                                            ? "#6d022f"
                                            : "#ac2e62"
                                    }
                                    stroke="none"
                                    strokeWidth={0}
                                    opacity={selectedBar && !isSelected ? 0.35 : 1}
                                    onMouseEnter={() => setHoveredBar(index)}
                                    onMouseLeave={() => setHoveredBar(null)}
                                />
                            );
                            })}
                            </Bar>
                            </BarChart>
                        </ResponsiveContainer>

                        <div className="d-flex gap-4 mt-3 pt-3" style={{ borderTop: "1px solid rgba(172,46,98,0.06)" }}>
                            <div>
                                <p className="mb-0" style={{ color: '#7f8c9b', fontSize: '0.78rem' }}>Total in Period</p>
                                <p className="mb-0 fw-bold" style={{ color: '#ac2e62', fontSize: '22px' }}>{chartData.reduce((sum, d) => sum + d.Visitors, 0)}</p>
                            </div>
                            <div>
                                <p className="mb-0" style={{ color: '#7f8c9b', fontSize: '0.78rem' }}>Average per {graphPeriod === "weekly" ? "Day" : graphPeriod === "monthly" ? "Month" : "Year"}</p>
                                <p className="mb-0 fw-bold" style={{ color: '#2c3e50', fontSize: '22px' }}>{chartData.length > 0 ? (chartData.reduce((sum, d) => sum + d.Visitors, 0) / chartData.length).toFixed(1) : 0}</p>
                            </div>
                        </div>

                        {selectedBar && (
                            <div style={{ marginTop: "24px", borderTop: "1px solid rgba(172,46,98,0.06)", paddingTop: "20px" }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <Activity size={16} style={{ color: '#ac2e62' }} />
                                        <h6 className="fw-bold mb-0" style={{ color: '#ac2e62' }}>
                                            Visitors for {selectedBar} ({selectedVisitors.length} unique)
                                        </h6>
                                    </div>
                                    <button onClick={() => { setSelectedBar(null); setSelectedVisitors([]); }} style={{ background: "rgba(0,0,0,0.04)", border: "none", color: "#999", cursor: "pointer", fontSize: "14px", width: "28px", height: "28px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                                </div>
                                {loadingVisitors ? (
                                    <div style={{ textAlign: 'center', padding: '20px' }}>
                                        <div style={{ display: 'inline-block', width: '24px', height: '24px', borderRadius: '50%', border: '2px solid rgba(172,46,98,0.1)', borderTopColor: '#ac2e62', animation: 'spin 0.6s linear infinite' }} />
                                        <p style={{ color: '#7f8c9b', fontSize: '0.85rem', marginTop: '8px' }}>Loading visitors...</p>
                                    </div>
                                ) : selectedVisitors.length === 0 ? (
                                    <p style={{ color: '#7f8c9b', textAlign: 'center', padding: '16px' }}>No visitors found for this period</p>
                                ) : (
                                    <div style={{ overflowX: "auto" }}>
                                        <Table hover size="sm" style={{ fontSize: "13px", marginBottom: 0 }}>
                                            <thead style={{ backgroundColor: 'rgba(172,46,98,0.04)' }}>
                                                <tr>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>Name</th>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>Mobile</th>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>Age</th>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>Purpose</th>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>Visit Time</th>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>Branch</th>
                                                    <th style={{ color: '#ac2e62', fontWeight: 600, borderBottom: '1px solid rgba(172,46,98,0.08)' }}>{periodVisitLabel}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedVisitors.map((v, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                                                        <td className="fw-bold" style={{ color: '#2c3e50' }}>{v.fullName}</td>
                                                        <td style={{ color: '#7f8c9b' }}>{v.mobileNumber}</td>
                                                        <td style={{ color: '#7f8c9b' }}>{v.dob ? `${calculateAge(v.dob)} yrs` : (v.ageGroup || '-')}</td>
                                                        <td><span className="db1-badge" style={{ background: 'rgba(172,46,98,0.06)', color: '#ac2e62', padding: '3px 10px' }}>{v.purposeOfVisit}</span></td>
                                                        <td style={{ color: '#7f8c9b' }}>{v.visitTime}</td>
                                                        <td style={{ color: '#7f8c9b' }}>{v.branch || '-'}</td>
                                                        <td style={{ color: "#ac2e62", fontWeight: "700" }}>{v.periodVisits}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default VisitorDashboard;
